import {  useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  Box,
  FormControl,
  OutlinedInput,
  Select,
  MenuItem,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';
import { storeAtom } from '../StoreManagement/ManageRestaurantTable';
import { useThemeColors } from '../../../Hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useEditStore } from '../../Query/ManageStoreQuery';
import LocationComponent from '../../stateless/Location';
import SelectStore from './AddStore';
import ImageUploadField from '../Utils/ImageUploadField';
import { Toaster } from 'react-hot-toast';
import { Field, Form, Formik } from 'formik';
import MyEditor from '../../stateless/RichTextEditor';
import MerchantNameField from '../StoreManagement/MerchantNameField';
import CategoryIdField from '../StoreManagement/CategoryIdField';
import StorePhoneNumberField from '../StoreManagement/StorePhoneNumberField';
import { uploadImageWithPresignedUrl } from '../../HelperFunction/s3Service';
import { resizeImage } from '../Utils/resizeImage';
import { useContext } from 'react';
import { HimmaContext } from '../../../Context/HimmaContext';
import { splitPhoneNumber } from '../../../utils/heplingFuctions';


// interface Option {
//   value: string;
//   label: string;
// }

const validationSchema = (t: any) => Yup.object().shape({
  merchantId: Yup.string().required(t('storeManagement.form.validation.requiredMerchant')),
  categoryId: Yup.array().of(Yup.string()).min(1, t('storeManagement.form.validation.requiredCategory')),
  storeName: Yup.string()
    .required(t('storeManagement.form.validation.requiredStoreName'))
    .matches(/^[A-Za-z\s]+$/, t('storeManagement.form.validation.storeNameNoNumbers'))
    .max(50, t('storeManagement.form.validation.storeNameMaxLength')),
  storeNameArabic: Yup.string()
    .required(t('storeManagement.form.validation.requiredStoreNameArabic'))
    .matches(
      /^[\u0600-\u06FF\s]+$/,
      t('storeManagement.form.validation.storeNameArabicOnlyArabic')
    )
    .max(50, t('storeManagement.form.validation.storeNameArabicMaxLength')),
  minOrderAmount: Yup.number()
    .typeError(t('storeManagement.form.validation.minimumOrderAmountNumber'))
    .min(0, t('storeManagement.form.validation.minimumOrderAmountMin'))
    .required(t('storeManagement.form.validation.requiredMinimumOrderAmount')),
  canPreOrder: Yup.string().required(t('storeManagement.form.validation.requiredPreOrderOption')),
  deliveryAvailable: Yup.string().required(
    t('storeManagement.form.validation.requiredPreOrderOption')
  ),
  hasSelfPickup: Yup.string().required(t('storeManagement.form.validation.requiredPreOrderOption')),
  additionalPackagingRequest: Yup.string().required(
    t('storeManagement.form.validation.requiredAdditionalCulinaryRequest')
  ),
  deliveryTime: Yup.string()
    .matches(/^\d+$/, t('storeManagement.form.validation.deliveryTimeNumeric'))
    .required(t('storeManagement.form.validation.requiredDeliveryTime')),
  // // additionalCulinaryRequest: Yup.string().required(
  // //   'Please select additional cutlery option'
  // // ),
  aboutStore: Yup.string()
      .test(
        'has-content',
        t('storeManagement.form.validation.requiredAboutStore'),
        value => {
          if (!value) return false;
          // Remove HTML tags and whitespace
          const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
          // Check for at least one letter or number
          return /[a-zA-Z0-9]/.test(text);
        }
      )
      .required(t('storeManagement.form.validation.requiredAboutStore')),
   aboutStoreArabic: Yup.string()
  .test(
    'has-arabic-content',
    t('storeManagement.form.validation.requiredAboutStoreArabic'),
    value => {
      if (!value) return false;
      const text = value
        .replace(/<[^>]*>/g, '')      // Remove HTML tags
        .replace(/&nbsp;/g, '')       // Remove &nbsp;
        .trim();

      // Check for at least one Arabic character and only Arabic-compatible content
      const arabicRegex = /^[\u0600-\u06FF\s0-9،؛؟.,\-!()]+$/; // Includes Arabic letters, digits, common punctuation
      return arabicRegex.test(text);
    }
  )
  .required(t('storeManagement.form.validation.requiredAboutStoreArabic')),
  deliveryRadiusKM: Yup.string()
    .matches(/^\d+$/, t('storeManagement.form.validation.deliveryRadiusNumeric'))
    .required(t('storeManagement.form.validation.requiredDeliveryRadius')),
  storePhoneNumber: Yup.string()
    .required(t('storeManagement.form.validation.requiredStorePhoneNumber'))
    .matches(/^[0-9]+$/, t('storeManagement.form.validation.storePhoneNumberNumeric'))
    .test(
      'len',
      t('storeManagement.form.validation.storePhoneNumberLength'),
      (val) => val?.length >= 9
    ),

  storeLogo: Yup.mixed()
    .required('Store logo is required')
    .test('fileSize', t('storeManagement.form.validation.logoSize'), (value: any) => {
      if (!value || typeof value === 'string') return true;
      return value.size <= 500000;
    })
    .test(
      'dimensions',
      t('storeManagement.form.validation.logoDimensions'),
      (value: any): Promise<boolean> => {
        if (!value || !(value instanceof File)) return Promise.resolve(true);

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
              const { width, height } = img;
              if (
                width >= 300 &&
                height >= 300 &&
                width <= 500 &&
                height <= 500
              ) {
                resolve(true);
              } else {
                resolve(false);
              }
            };

            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value);
        });
      }
    ),
  storeBanner: Yup.array()
    .min(1, 'At least one banner image is required')
    .max(6, 'Maximum 6 banner images allowed')
    .test(
      'fileSize',
      'Each banner must be less than 1MB',
      (value: any[] | undefined) => {
        if (!value || !Array.isArray(value)) return true;
        return value.every((file) => {
          if (typeof file === 'string') return true;
          return file.size <= 1 * 1024 * 1024;
        });
      }
    )
    .test(
      'dimensions',
      'Image dimensions must be between 1366×300 and 1500×500',
      async (value: any[] | undefined): Promise<boolean> => {
        if (!value || !Array.isArray(value)) return true;

        const dimensionChecks = await Promise.all(
          value.map(async (file) => {
            if (typeof file === 'string') return { isValid: true };
            if (!(file instanceof File)) return { isValid: true };

            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                  const { width, height } = img;
                  const isValid =
                    width >= 1366 &&
                    width <= 1500 &&
                    height >= 300 &&
                    height <= 500;
                  resolve({ isValid, width, height });
                };
                img.onerror = () => resolve({ isValid: false });
                img.src = event.target?.result as string;
              };
              reader.onerror = () => resolve({ isValid: false });
              try {
                reader.readAsDataURL(file);
              } catch (error) {
                console.error('Error reading file:', error);
                resolve({ isValid: false });
              }
            });
          })
        );

        const invalidDimensions = dimensionChecks
          .filter((check: any) => !check.isValid && check.width && check.height)
          .map((check: any) => `${check.width}×${check.height}`);

        if (invalidDimensions.length > 0) {
          return false;
        }

        return true;
      }
    ),
  workingHours: Yup.object({
    sunday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    monday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    tuesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    wednesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    thursday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    friday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
    saturday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }),
  }),

  deliveryTimeUnit: Yup.string().required('Please select time unit'),
  storeAddress: Yup.string().required('Please enter restaurant address'),
  latitude: Yup.string().required('Latitude is required'),
  longitude: Yup.string().required('Longitude is required'),
});

// const timeToNumber = (time: string | undefined): number => {
//   if (!time) return -1; // invalid or unset times return -1
//   // Handle both :00Z and :59Z formats
//   const cleanTime = time.replace(':00Z', '').replace(':59Z', ':59').replace(':', '');
//   return parseInt(cleanTime);
// };

const generateTimeOptions = () => {
  const times = [];
  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 30) {
      const hour = hours.toString().padStart(2, '0');
      const minute = minutes.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  // Add 23:59 as a special case
  times.push('23:59');
  return times;
};
type AddRestaurantProps = {
  onCancel: () => void;
  storeType: string;
  edit: string;
};



const AddGrocery = ({ onCancel, storeType, edit }: AddRestaurantProps) => {
  // const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const { warning } = useThemeColors();
  const [storeId] = useAtom<any>(storeAtom);
  const apiClient = useContext(HimmaContext);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // const countryCodes = [
  //   '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'
  // ];
  const fullNumber = storeId?.restaurantDetails?.storePhoneNumber || '';
  // const matchedCode = countryCodes.find(code => fullNumber.startsWith(code));
  // const phoneNumber = matchedCode ? fullNumber.slice(matchedCode.length) : '';
  
  const {countryCode:matchedCode, phone:phoneNumber} = splitPhoneNumber(fullNumber)
  const [selectedCountryCode, setSelectedCountryCode] = useState(matchedCode || '+962');

  const { mutate: EditStore } = useEditStore();
  // const { mutate: CreateStore, isPending: isCreateStoreLoading } =
  //   UseCreateStores();

  // const { data: merchantData } = useAllMerchant({
  //   initialData: {
  //     storeType: 'GroceryStore',
  //   },
  // });

  // const merchantOptions = merchantData?.map((item: MerchantData) => ({
  //   value: item.id,
  //   label: item.name,
  // }));

  // const { data: categoryData } = useAllCategory({
  //   initialData: {
  //     storeType: storeType,
  //   },
  // });

  // const categoryOptions = categoryData?.map((item: CategoryData) => ({
  //   value: item.id,
  //   label: item.nameEnglish,
  // }));


  const categoryIds = storeId?.restaurantDetails?.categoryId || [];

  const initialValues = {
    categoryId: storeId ? (Array.isArray(categoryIds) ? categoryIds : [categoryIds]) : [],
    categoryName: storeId ? storeId?.restaurantCategory : '',
    storeAddress: storeId ? storeId?.restaurantDetails?.storeAddress?.address : '',
    storeName: storeId ? storeId.nameEnglish : '',
    merchantId: storeId ? storeId.restaurantDetails.merchantId : '',
    merchantName: storeId ? storeId.merchantName : '',
    storeNameArabic: storeId ? storeId.nameArabic : '',
    additionalPackagingRequest: storeId ? (storeId.restaurantDetails.additionalPackagingRequest ? 'Yes' : 'No') : 'No',
    minOrderAmount: storeId ? storeId.restaurantDetails.minOrderAmount.value : null,
    canPreOrder: storeId ? (storeId.restaurantDetails.canPreOrder ? 'Yes' : 'No') : 'No',
    // IsHalal: storeId ? (storeId.restaurantDetails.isHalal ? 'Yes' : 'No') : '',
    // restaurantType: storeId ? storeId.restaurantDetails.restaurantType.map((item: string) => item.toLowerCase()) : [],
    preferableTime: storeId ? storeId.restaurantDetails.preferableTime.map((item: string) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()) : [],
    hasSelfPickup: storeId ? (storeId.restaurantDetails.hasSelfPickup ? 'Yes' : 'No') : 'No',
    deliveryAvailable: storeId ? (storeId.restaurantDetails.deliveryAvailable ? 'Yes' : 'No') : 'No',
    deliveryTime: storeId ? storeId.restaurantDetails.deliveryTime.value : '',
    deliveryTimeUnit: storeId ? (storeId.restaurantDetails.deliveryTime.format.charAt(0).toUpperCase() + storeId.restaurantDetails.deliveryTime.format.slice(1)) : '',
    // additionalCulinaryRequest: storeId ? (storeId.restaurantDetails.additionalCulinaryRequest ? 'Yes' : 'No') : '',
    aboutStore: storeId ? storeId.restaurantDetails.aboutStore : '',
    aboutStoreArabic: storeId ? storeId.restaurantDetails.aboutStoreArabic : '',
    deliveryRadiusKM: storeId ? storeId.restaurantDetails.deliveryRadiusKM : '',
    storePhoneNumber: phoneNumber,
    storeLogo: storeId ? storeId.restaurantDetails.storeLogo : null,
    storeBanner: storeId ? storeId.restaurantDetails.storeBanner : [],
    latitude: storeId ? storeId.restaurantDetails?.storeAddress?.latitude : '',
    longitude: storeId ? storeId.restaurantDetails?.storeAddress?.longitude : '',
    location: storeId ? {
      address: storeId?.restaurantDetails?.storeAddress.address,
      latitude: storeId?.restaurantDetails?.storeAddress.latitude,
      longitude: storeId?.restaurantDetails?.storeAddress.longitude,
    } : {
      address: '',
      latitude: '',
      longitude: '',
    },
    deliveryFee: storeId?.restaurantDetails?.deliveryFee || {
      deliveryChargeSetting: {
        type: 'default',
        deliveryFeeStatus: {
          type: 'disable',
          commonDeliveryFee: { type: 'disable', commonDeliveryFeeValue: '' },
          feePerKM: { type: 'disable', feePerKMValue: '' },
        },
        peakHourDeliveryFeeStatus: {
          type: 'disable',
          data: {
            sunday: [],
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
          },
        },
      },
    },
    workingHours: {
      sunday: storeId?.restaurantDetails?.workingHours?.sunday || { isOpen: true, startTime: '', endTime: '' },
      monday: storeId?.restaurantDetails?.workingHours?.monday || { isOpen: true, startTime: '', endTime: '' },
      tuesday: storeId?.restaurantDetails?.workingHours?.tuesday || { isOpen: true, startTime: '', endTime: '' },
      wednesday: storeId?.restaurantDetails?.workingHours?.wednesday || { isOpen: true, startTime: '', endTime: '' },
      thursday: storeId?.restaurantDetails?.workingHours?.thursday || { isOpen: true, startTime: '', endTime: '' },
      friday: storeId?.restaurantDetails?.workingHours?.friday || { isOpen: true, startTime: '', endTime: '' },
      saturday: storeId?.restaurantDetails?.workingHours?.saturday || { isOpen: true, startTime: '', endTime: '' },
    },
  };

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      if (!apiClient) {
        throw new Error('API client not available');
      }

      // Handle logo upload
      let logoUrl = values.storeLogo;
      if (values.storeLogo instanceof File) {
        const resizedLogo = await resizeImage(values.storeLogo, "store logo");
        const { url } = await uploadImageWithPresignedUrl(resizedLogo, apiClient);
        logoUrl = url;
      }

      // Handle banner uploads
      let bannerUrls = values.storeBanner;
      if (values.storeBanner && values.storeBanner.length > 0) {
        const filesToUpload = values.storeBanner.filter((file: File | string) => file instanceof File) as File[];
        if (filesToUpload.length > 0) {
          const resizedBanners = await Promise.all(
            filesToUpload.map((file: File) => resizeImage(file, "store banner"))
          );
          const uploadedBanners = await Promise.all(
            resizedBanners.map((file: File) => uploadImageWithPresignedUrl(file, apiClient))
          );
          const uploadedUrls = uploadedBanners.map((result: { url: string }) => result.url);
          
          // Replace File objects with URLs, keep existing URLs
          bannerUrls = values.storeBanner.map((item: File | string) => 
            item instanceof File ? uploadedUrls.shift() || item : item
          );
        }
      }

      const formattedData = {
        ...values,
        storePhoneNumber: `${selectedCountryCode}${values.storePhoneNumber}`,
        storeName: values.storeName,
        storeNameArabic: values.storeNameArabic,
        minOrderAmount: {
          currency: 'JOD',
          value: +values.minOrderAmount,
        },
        canPreOrder: values.canPreOrder === 'Yes',
        additionalPackagingRequest:
          values.additionalPackagingRequest === 'Yes',
        preferableTime: values.preferableTime.map((item: string) => item.toLowerCase()),
        storeType: storeType,
        hasSelfPickup: values.hasSelfPickup === 'Yes',
        deliveryAvailable: values.deliveryAvailable === 'Yes',
        deliveryTime: {
          value: +values.deliveryTime,
          format: values?.deliveryTimeUnit?.toLowerCase(),
        },

        aboutStore: values.aboutStore,
        aboutStoreArabic: values.aboutStoreArabic,
        storeAddress: {
          address: values.storeAddress || '',
          latitude: values.latitude || '',
          longitude: values.longitude || '',
        },
        workingHours: values.workingHours,
        deliveryRadiusKM: values.deliveryRadiusKM,
        storeLogo: logoUrl,
        storeBanner: bannerUrls,
      };
      EditStore(
        {
          id: storeId.id,
          data: formattedData,
        },
        {
          onSuccess: () => {
            onCancel();
          },
          onError: (error) => {
            console.error('EditStore error:', error);
          },
        }
      );
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const logAndValidate = async (values: typeof initialValues) => {
    try {
      // Use Yup's validation schema to validate
      await validationSchema(t).validate(values, { abortEarly: false });
      return {};
    } catch (err: any) {
      if (err && err.inner) {
        const errorMap: Record<string, string> = {};
        err.inner.forEach((validationError: any) => {
          if (validationError.path && !errorMap[validationError.path]) {
            errorMap[validationError.path] = validationError.message;
          }
        });
        // Log all errors to the console
        console.error('Formik Validation Errors:', errorMap);
        return errorMap;
      }
      // Log any other error
      console.error('Formik Validation Error:', err);
      return {};
    }
  };


  return (
    <>
      <Toaster position="top-right" />
      {edit === 'edit' && (
        <SelectStore
          onCancel={onCancel}
          storeTypeOnEdit={storeType ? [storeType as "GroceryStore" | "PharmacyStore" | "Restaurant"] : undefined}
        />

      )}
      <Box
        maxWidth={800}
        mx="auto"
        my={4}
        p={{ xs: 2, sm: 4 }}
        bgcolor="#fff"
        borderRadius={3}
        boxShadow={2}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          enableReinitialize
          onSubmit={handleSubmit}
          validate={logAndValidate}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched,submitCount }) => (
            <Form>
              {/* Merchant Name */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.merchantName')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field name="merchantName" component={MerchantNameField} storeId={storeId} />
                </Box>
              </Box>
              {/* Category */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.category')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field name="categoryId" component={CategoryIdField} storeId={storeId} />
                  {touched.categoryId && typeof errors.categoryId === 'string' && (
                    <Typography variant="caption" color="error">{errors.categoryId}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Name */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeName')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.storeName && Boolean(errors.storeName)} fullWidth>
                    <OutlinedInput
                      name="storeName"
                      value={values.storeName}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 50) {
                          setFieldValue('storeName', newValue);
                          setFieldTouched('storeName', true);
                        } else {
                          setFieldValue('storeName', newValue.slice(0, 50));
                        }
                      }}
                      onBlur={() => setFieldTouched('storeName', true)}
                      placeholder="Enter Restaurant Name"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />
                  </FormControl>
                  {touched.storeName && typeof errors.storeName === 'string' && (
                    <Typography variant="caption" color="error">{errors.storeName}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Name Arabic */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeNameArabic')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.storeNameArabic && Boolean(errors.storeNameArabic)} fullWidth>
                    <OutlinedInput
                      name="storeNameArabic"
                      value={values.storeNameArabic}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue.length <= 50) {
                          setFieldValue('storeNameArabic', newValue);
                          setFieldTouched('storeNameArabic', true);
                        } else {
                          setFieldValue('storeNameArabic', newValue.slice(0, 50));
                        }
                      }}
                      onBlur={() => setFieldTouched('storeNameArabic', true)}
                      placeholder="Enter Restaurant Name in Arabic"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />
                  </FormControl>
                  {touched.storeNameArabic && typeof errors.storeNameArabic === 'string' && (
                    <Typography variant="caption" color="error">{errors.storeNameArabic}</Typography>
                  )}
                </Box>
              </Box>
              {/* Minimum Order Amount */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.minimumOrderAmount')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.minOrderAmount && Boolean(errors.minOrderAmount)} fullWidth>
                    <Box sx={{ display: 'flex', gap: '4px', width: { xs: '100%', sm: '400px' } }}>
                      <Box sx={{ width: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>JOD</Box>
                      <OutlinedInput
                        type="number"
                        value={values.minOrderAmount}
                        onChange={(e) => {
                          setFieldValue('minimumOrderAmount', e.target.value);
                          setFieldTouched('minimumOrderAmount', true, false);
                        }}
                        placeholder="Enter Minimum Order Amount"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </FormControl>
                  {touched.minOrderAmount && typeof errors.minOrderAmount === 'string' && (
                    <Typography variant="caption" color="error">{errors.minOrderAmount}</Typography>
                  )}
                </Box>
              </Box>
              {/* Pre-order Option */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.preOrderOption')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.canPreOrder && Boolean(errors.canPreOrder)} fullWidth>
                    <RadioGroup
                      row
                      name="canPreOrder"
                      value={values.canPreOrder}
                      onChange={(e) => setFieldValue('canPreOrder', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.canPreOrder && typeof errors.canPreOrder === 'string' && (
                      <Typography variant="caption" color="error">{errors.canPreOrder}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* additional packaging request */}
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}
              >
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">
                    Additional Package Request <span style={{ color: 'red' }}>*</span>
                  </Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.additionalPackagingRequest && Boolean(errors.additionalPackagingRequest)} fullWidth>
                    <RadioGroup
                      row
                      name="additionalPackagingRequest"
                      value={values.additionalPackagingRequest}
                      onChange={(e) => setFieldValue('additionalPackagingRequest', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.additionalPackagingRequest && typeof errors.additionalPackagingRequest === 'string' && (
                      <Typography variant="caption" color="error">
                        {errors.additionalPackagingRequest}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Self Pickup */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.selfPickup')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.hasSelfPickup && Boolean(errors.hasSelfPickup)} fullWidth>
                    <RadioGroup
                      row
                      name="hasSelfPickup"
                      value={values.hasSelfPickup}
                      onChange={(e) => setFieldValue('hasSelfPickup', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.hasSelfPickup && typeof errors.hasSelfPickup === 'string' && (
                      <Typography variant="caption" color="error">{errors.hasSelfPickup}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Delivery Available */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.preOrderOption')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.deliveryAvailable && Boolean(errors.deliveryAvailable)} fullWidth>
                    <RadioGroup
                      row
                      name="deliveryAvailable"
                      value={values.deliveryAvailable}
                      onChange={(e) => setFieldValue('deliveryAvailable', e.target.value)}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label={t('storeManagement.form.yes')} />
                      <FormControlLabel value="No" control={<Radio />} label={t('storeManagement.form.no')} />
                    </RadioGroup>
                    {touched.deliveryAvailable && typeof errors.deliveryAvailable === 'string' && (
                      <Typography variant="caption" color="error">{errors.deliveryAvailable}</Typography>
                    )}
                  </FormControl>
                </Box>
              </Box>
              {/* Delivery Fee - hidden configuration, defaults applied internally */}
              {/* <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Delivery Fee <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <DeliveryFeeForm field={{ name: 'deliveryFee', value: values.deliveryFee }} form={{ values, setFieldValue }} errors={errors} touched={touched} />
                </Box>
              </Box> */}
              {/* Response Time and Unit (combined row) */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.responseTime')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', sm: '400px' } }}>
                    <FormControl error={touched.deliveryTime && Boolean(errors.deliveryTime)} sx={{ flex: 1 }}>
                      <OutlinedInput
                        name="deliveryTime"
                        value={values.deliveryTime}
                        onChange={(e) => setFieldValue('deliveryTime', e.target.value)}
                        onBlur={() => setFieldTouched('deliveryTime', true)}
                        onKeyDown={(e) => {
                          const isDigit = /^[0-9]$/.test(e.key);
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                          ];
                          if (!isDigit && !allowedKeys.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder={t('storeManagement.form.placeholders.deliveryTime')}
                      />
                    </FormControl>
                    <FormControl error={touched.deliveryTimeUnit && Boolean(errors.deliveryTimeUnit)} sx={{ minWidth: 120 }}>
                      <Select
                        value={values.deliveryTimeUnit}
                        onChange={(e) => setFieldValue('deliveryTimeUnit', e.target.value)}
                        displayEmpty
                        input={<OutlinedInput />}
                      >
                        <MenuItem value="">
                          <em>Select Unit</em>
                        </MenuItem>
                        <MenuItem value="Minutes">Minutes</MenuItem>
                        <MenuItem value="Hours">Hours</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {(touched.deliveryTime && typeof errors.deliveryTime === 'string') && (
                    <Typography variant="caption" color="error">{errors.deliveryTime}</Typography>
                  )}
                  {(touched.deliveryTimeUnit && typeof errors.deliveryTimeUnit === 'string') && (
                    <Typography variant="caption" color="error">{errors.deliveryTimeUnit}</Typography>
                  )}
                </Box>
              </Box>
              {/* About Store */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">About Store <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <MyEditor
                    value={values.aboutStore}
                    onChange={(val: string) => setFieldValue('aboutStore', val)}
                    onBlur={() => setFieldTouched('aboutStore', true)}
                  />
                  {((touched.aboutStore || submitCount > 0)) && typeof errors.aboutStore === 'string' && (
                    <Typography variant="caption" color="error">{errors.aboutStore}</Typography>
                  )}
                </Box>
              </Box>
              {/* About Store Arabic */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">About Store (Arabic) <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <MyEditor
                    value={values.aboutStoreArabic}
                    onChange={(val: string) => setFieldValue('aboutStoreArabic', val)}
                    onBlur={() => setFieldTouched('aboutStoreArabic', true)}
                  />
                  {((touched.aboutStore || submitCount > 0))&& typeof errors.aboutStoreArabic === 'string' && (
                    <Typography variant="caption" color="error">{errors.aboutStoreArabic}</Typography>
                  )}
                </Box>
              </Box>
              {/* Delivery Radius */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.deliveryRadiusKM')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl error={touched.deliveryRadiusKM && Boolean(errors.deliveryRadiusKM)} fullWidth>
                    <OutlinedInput
                      name="deliveryRadiusKM"
                      value={values.deliveryRadiusKM}
                      onChange={(e) => setFieldValue('deliveryRadiusKM', e.target.value)}
                      onBlur={() => setFieldTouched('deliveryRadiusKM', true)}
                      onKeyDown={(e) => {
                        const isDigit = /^[0-9]$/.test(e.key);
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'Tab',
                          'ArrowLeft',
                          'ArrowRight',
                        ];
                        if (!isDigit && !allowedKeys.includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Enter Delivery Radius"
                      sx={{ width: { xs: '100%', sm: '400px' } }}
                    />

                  </FormControl>
                  {touched.deliveryRadiusKM && typeof errors.deliveryRadiusKM === 'string' && (
                    <Typography variant="caption" color="error">{errors.deliveryRadiusKM}</Typography>
                  )}
                </Box>
              </Box>
              {/* Store Phone Number */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Store Phone Number <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Field
                    name="storePhoneNumber"
                    component={StorePhoneNumberField}
                    selectedCountryCode={selectedCountryCode}
                    onCountryCodeChange={setSelectedCountryCode}
                  />
                </Box>
              </Box>
              {/* Store Address */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeAddress')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  {/* <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Store Address</Typography> */}
                  <LocationComponent
                    field={{ name: 'storeAddress', value: values.storeAddress }}
                    form={{ values, errors, touched, setFieldValue, setFieldTouched }}
                  />
                </Box>
              </Box>
             {/* Store Logo */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.storeLogo')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box mt={2}>
                    <ImageUploadField name="storeLogo" label={t('storeManagement.form.storeLogo')} />
                    {(touched.storeLogo || values.storeLogo) && typeof errors.storeLogo === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeLogo}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Store Banner */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.banner')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <Box mt={2}>
                    <ImageUploadField name="storeBanner" label={t('storeManagement.form.banner')} multiple maxFiles={6} recommendedText="Max 6 images" />
                    {(touched.storeBanner || (values.storeBanner && values.storeBanner.length > 0)) && typeof errors.storeBanner === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeBanner}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Working Hours */}
              <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">{t('storeManagement.form.workingHours')} <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                <Box flex={1} width="100%">
                  <FormControl fullWidth>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      {(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const).map((dayKey) => {
                        const dayData = values.workingHours[dayKey];
                        const dayErrors = (errors?.workingHours && (errors.workingHours as any)[dayKey]) || {};
                        const dayTouched = (touched?.workingHours && (touched.workingHours as any)[dayKey]) || {};
                        return (
                          <Box key={dayKey} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ minWidth: { xs: 80, sm: 100 }, maxWidth: { xs: 120, sm: 140 }, mr: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography sx={{ color: 'text.secondary', fontSize: 14, whiteSpace: 'nowrap' }}>{t(`storeManagement.form.${dayKey}`)} *</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!dayData.isOpen}
                                    onChange={(e) => {
                                      setFieldValue(`workingHours.${dayKey}.isOpen`, !e.target.checked);
                                      if (e.target.checked) {
                                        setFieldValue(`workingHours.${dayKey}.startTime`, '00:00:00Z');
                                        setFieldValue(`workingHours.${dayKey}.endTime`, '00:00:00Z');
                                      }
                                      setFieldTouched(`workingHours.${dayKey}`, true, false);
                                    }}
                                    size="small"
                                  />
                                }
                                label={<Typography variant="body2" color="text.secondary">{t('storeManagement.form.closed')}</Typography>}
                              />
                              {dayData.isOpen && (
                                <Box sx={{ display: 'flex', gap: 4 }}>
                                  {/* Start Time Select */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl error={Boolean(dayTouched?.startTime && dayErrors?.startTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                      <Select
                                        value={dayData.startTime ? dayData.startTime.replace(':00Z', '').replace(':59Z', '') : ''}
                                        onChange={(e) => {
                                          const timeValue = e.target.value === '23:59' ? `${e.target.value}:59Z` : (e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                          setFieldValue(`workingHours.${dayKey}.startTime`, timeValue);
                                          setFieldTouched(`workingHours.${dayKey}.startTime`, true, false);
                                        }}
                                        onBlur={() => setFieldTouched(`workingHours.${dayKey}.startTime`, true, false)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>{t('storeManagement.form.selectStart')}</em>
                                        </MenuItem>
                                        {generateTimeOptions().map((time) => (
                                          <MenuItem key={time} value={time}>{time}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    {dayTouched?.startTime && typeof dayErrors?.startTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.startTime}</Typography>
                                    )}
                                  </Box>
                                  {/* End Time Select */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl error={Boolean(dayTouched?.endTime && dayErrors?.endTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                      <Select
                                        value={dayData.endTime ? dayData.endTime.replace(':00Z', '').replace(':59Z', '') : ''}
                                        onChange={(e) => {
                                          const timeValue = e.target.value === '23:59' ? `${e.target.value}:59Z` : (e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                          setFieldValue(`workingHours.${dayKey}.endTime`, timeValue);
                                          setFieldTouched(`workingHours.${dayKey}.endTime`, true, false);
                                        }}
                                        onBlur={() => setFieldTouched(`workingHours.${dayKey}.endTime`, true, false)}
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>{t('storeManagement.form.selectEnd')}</em>
                                        </MenuItem>
                                        {generateTimeOptions().map((time) => (
                                          <MenuItem key={time} value={time}>{time}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    {dayTouched?.endTime && typeof dayErrors?.endTime === 'string' && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.endTime}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </FormControl>
                </Box>
              </Box>
              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center', width: '100%', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: 'none', fontSize: 14, px: 3, py: 1, backgroundColor: COLORS.primary.main, '&:hover': { backgroundColor: 'rgba(76, 10, 39, 0.8)' }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (<><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />{t('storeItem.form.updating')}</>) : (t('common.update') || 'Update')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={onCancel}
                  sx={{ bgcolor: warning.main, color: '#fff', '&:hover': { bgcolor: warning.dark, color: '#fff' } }}
                >
                  {t('storeItem.form.cancel')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default AddGrocery;
